/**
 * US-113: экран «Ставки» — история ставки сотрудника.
 *
 * Кто видит: роль «Ведение ставок» (`rate_manage`) + admin/staff — флаг
 * `can_manage_rates` в `/api/me/`. Сотруднику ставка не показывается (решение
 * владелицы 23.09), поэтому экрана «моя ставка» нет.
 *
 * Поиск идёт по всей компании, включая уволенных (`?purpose=rates`): переводы
 * оформляют и задним числом. Выбранный сотрудник живёт в URL
 * (`/app/rates?employee=<id>`), чтобы на экран можно было дать ссылку.
 *
 * Каждое изменение (перевод, правка, удаление) идёт в два шага: форма →
 * подтверждение. На шаге подтверждения показано то, что система сама НЕ
 * исправит (`/rates/impact/`): согласованные недели, автозаполненные недели и
 * месяцы, за которые уже дозаполнялась выгрузка в 1С.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  List,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { Pencil, Trash2 } from "lucide-react";

import {
  createEmployeeRate,
  deleteEmployeeRate,
  fetchEmployeeRates,
  fetchRateImpact,
  searchRateEmployees,
  updateEmployeeRate,
  type EmployeeRates,
  type EmploymentRateRow,
  type RateImpact,
  type RateInput,
} from "../api/client";
import { formatHours, formatRate } from "../utils/format";
import { parseRuDate } from "./AbsenceForm";
import { STATUS_LABEL_MAPS } from "./StatusBadge";

dayjs.locale("ru");

// ---------------------------------------------------------------------------
// Helpers (экспортируются для unit-тестов)
// ---------------------------------------------------------------------------

const fmtDate = (iso: string | null | undefined): string =>
  iso ? dayjs(iso).format("DD.MM.YYYY") : "";

/** «2026-08» → «Август 2026». */
export function formatMonth(monthKey: string): string {
  const s = dayjs(`${monthKey}-01`).format("MMMM YYYY");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Проверка ставки до обращения к серверу — те же правила и тексты, что у
 * `apps.core.rates.parse_rate`. Возвращает `{ value }` («0.50») или `{ error }`.
 */
export function parseRateInput(text: string): { value: string } | { error: string } {
  const raw = text.trim().replace(",", ".");
  if (!raw) return { error: "Укажите ставку." };
  if (!/^\d+(\.\d+)?$/.test(raw) && !/^\.\d+$/.test(raw)) {
    return { error: "Ставка должна быть числом, например 0,5." };
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) return { error: "Ставка должна быть числом, например 0,5." };
  if (Math.abs(n * 100 - Math.round(n * 100)) > 1e-9) {
    return { error: "Ставка задаётся не точнее сотых: например 0,75." };
  }
  if (n < 0.01 || n > 1) return { error: "Ставка должна быть от 0,01 до 1." };
  return { value: n.toFixed(2) };
}

function statusLabel(status: string): string {
  return (STATUS_LABEL_MAPS.timesheet as Record<string, string>)[status] ?? status;
}

/** «Затронутый период: …» — интервал, который вернул сервер. */
export function impactPeriodText(impact: RateImpact): string {
  const from = impact.date_from ? `с ${fmtDate(impact.date_from)}` : "с начала работы";
  const to = !impact.date_to
    ? ", без даты окончания"
    : impact.ends_at_termination
      ? ` по ${fmtDate(impact.date_to)} (дата увольнения)`
      : ` по ${fmtDate(impact.date_to)} (далее действует следующий перевод)`;
  return `Затронутый период: ${from}${to}.`;
}

// ---------------------------------------------------------------------------
// Предупреждение: что НЕ пересчитается
// ---------------------------------------------------------------------------

interface RateImpactNoticeProps {
  impact: RateImpact;
  /** Операция заводит строку «с начала» сотруднику, у которого её не было:
   *  до сохранения ставка на эти дни не задана. */
  createsInitial?: boolean;
}

export function RateImpactNotice({ impact, createsInitial = false }: RateImpactNoticeProps) {
  // Месяцы, где сотрудник в дозаполнение не входил, перевод почти никогда не
  // меняет: недостачи у него не было. Исключение — заведение строки «с начала»:
  // до неё ставка не была задана, и дозаполнение его не считало. Войдёт ли он
  // в состав после сохранения, не гарантировано (нужны недостача, правило
  // распределения и проект с меткой 1С) — поэтому «рассчитает заново».
  const includedMonths = impact.zup_fill_months.filter((m) => m.includes_employee);
  const skippedMonths = createsInitial
    ? impact.zup_fill_months.filter((m) => !m.includes_employee)
    : [];
  const period = (
    <Text size="sm" data-testid="rate-impact-period">
      {impactPeriodText(impact)}
    </Text>
  );
  const recalcNote = (
    <Text size="sm">
      Норма несогласованных недель, затронутых изменением, будет пересчитана автоматически.
    </Text>
  );
  const monthItem = (m: RateImpact["zup_fill_months"][number]) => (
    <List.Item key={m.month}>
      {formatMonth(m.month)}
      {m.last_filled_at ? ` (последнее дозаполнение — ${fmtDate(m.last_filled_at)})` : ""}
    </List.Item>
  );
  // Перевод с датой позже увольнения: периода нет (иначе вышло бы «с 01.10 по
  // 20.09»), норму он не меняет — так и говорим.
  if (impact.after_termination) {
    return (
      <Text size="sm" data-testid="rate-impact-after-termination">
        Перевод начинается после даты увольнения ({fmtDate(impact.termination_date)}) и на норму не
        влияет.
      </Text>
    );
  }
  if (
    impact.approved_weeks.length === 0 &&
    impact.autofilled_weeks.length === 0 &&
    includedMonths.length === 0 &&
    skippedMonths.length === 0
  ) {
    return (
      <Stack gap={4} data-testid="rate-impact-empty">
        {period}
        {recalcNote}
        <Text size="sm" c="dimmed">
          Согласованных недель, недель с автоматическим заполнением и месяцев с дозаполнением
          выгрузки в 1С, затронутых изменением, нет.
        </Text>
      </Stack>
    );
  }
  return (
    <Alert color="yellow" title="Проверьте последствия до сохранения" data-testid="rate-impact">
      <Stack gap="sm">
        {period}
        {recalcNote}
        {impact.approved_weeks.length > 0 && (
          <div data-testid="rate-impact-approved">
            <Text size="sm" fw={500}>
              Согласованные недели не будут пересчитаны: изменение ставки к ним не применяется,
              норма остаётся прежней ({impact.approved_weeks.length}):
            </Text>
            <List size="sm" withPadding>
              {impact.approved_weeks.map((w) => (
                <List.Item key={w.id}>
                  {fmtDate(w.week_start)} – {fmtDate(w.week_end)}, норма{" "}
                  {formatHours(w.required_hours)} ч
                </List.Item>
              ))}
            </List>
          </div>
        )}
        {impact.autofilled_weeks.length > 0 && (
          <div data-testid="rate-impact-autofilled">
            <Text size="sm" fw={500}>
              Недели, заполненные системой автоматически, будут пересчитаны по истории ставки;
              проставленные в них часы не изменятся. Эти недели необходимо проверить (
              {impact.autofilled_weeks.length}):
            </Text>
            <List size="sm" withPadding>
              {impact.autofilled_weeks.map((w) => (
                <List.Item key={w.id}>
                  {fmtDate(w.week_start)} – {dayjs(w.week_start).add(6, "day").format("DD.MM.YYYY")}
                  , списано {formatHours(w.allocated_hours)} ч, статус: {statusLabel(w.status)}
                </List.Item>
              ))}
            </List>
          </div>
        )}
        {includedMonths.length > 0 && (
          <div data-testid="rate-impact-zup">
            <Text size="sm" fw={500}>
              За эти месяцы уже выполнялось дозаполнение выгрузки в 1С, и сотрудник входил в его
              состав. Повторное дозаполнение может дать другие значения для этого сотрудника (
              {includedMonths.length}):
            </Text>
            <List size="sm" withPadding>
              {includedMonths.map(monthItem)}
            </List>
          </div>
        )}
        {skippedMonths.length > 0 && (
          <div data-testid="rate-impact-zup-skipped">
            <Text size="sm" fw={500}>
              За эти месяцы уже выполнялось дозаполнение выгрузки в 1С, и сотрудник в его состав не
              вошёл: ставка на эти дни не была задана. После сохранения повторное дозаполнение
              рассчитает его заново ({skippedMonths.length}):
            </Text>
            <List size="sm" withPadding>
              {skippedMonths.map(monthItem)}
            </List>
          </div>
        )}
      </Stack>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Окно перевода / правки / удаления
// ---------------------------------------------------------------------------

type EditorTarget =
  | { mode: "create" }
  | { mode: "edit"; row: EmploymentRateRow }
  | { mode: "delete"; row: EmploymentRateRow };

interface RateEditorProps {
  employeeId: number;
  target: EditorTarget;
  /** Вся история сотрудника — чтобы знать, есть ли ставка до удаляемой строки. */
  rows: EmploymentRateRow[];
  /** У сотрудника нет строки «с начала» (заведён в обход) — перевод без даты
   *  заводит её. Иначе дата обязательна. */
  allowInitial: boolean;
  onClose: () => void;
  onSaved: (data: EmployeeRates, message: string) => void;
}

const MODAL_TITLE: Record<EditorTarget["mode"], string> = {
  create: "Оформить перевод",
  edit: "Изменить строку истории",
  delete: "Удалить перевод",
};

function RateEditor({ employeeId, target, rows, allowInitial, onClose, onSaved }: RateEditorProps) {
  const row = target.mode === "create" ? null : target.row;
  const isInitial = row?.is_initial ?? false;
  const [rateText, setRateText] = useState(row ? formatRate(row.rate) : "");
  const [dateFrom, setDateFrom] = useState<Date | null>(
    row?.date_from ? dayjs(row.date_from).toDate() : null,
  );
  const [note, setNote] = useState(row?.note ?? "");
  const [step, setStep] = useState<"form" | "confirm">(
    target.mode === "delete" ? "confirm" : "form",
  );
  // noteOnly — ставка и дата не менялись: сервер ничего не пересчитывает, и
  // шаг последствий не нужен (разбор MR !364, п. 4).
  const [pending, setPending] = useState<{ body: RateInput; noteOnly: boolean } | null>(null);
  const [impact, setImpact] = useState<RateImpact | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `rowId` — строка, которую правят или удаляют: сервер считает последствия
  // от меньшей из старой и новой даты до следующего перевода, не считая её.
  const loadImpact = useCallback(
    (dateFrom: string | null, rowId?: number) => {
      setBusy(true);
      setError(null);
      setImpact(null);
      return fetchRateImpact(employeeId, dateFrom, rowId)
        .then((data) => {
          setImpact(data);
          return true;
        })
        .catch((e: Error) => {
          setError(e.message || "Не удалось проверить последствия изменения.");
          return false;
        })
        .finally(() => setBusy(false));
    },
    [employeeId],
  );

  const loadDeleteImpact = useCallback(() => {
    if (target.mode === "delete") void loadImpact(target.row.date_from, target.row.id);
  }, [target, loadImpact]);

  // Удаление: сразу шаг подтверждения — последствия от даты строки.
  useEffect(() => {
    loadDeleteImpact();
  }, [loadDeleteImpact]);

  const handleNext = () => {
    const parsed = parseRateInput(rateText);
    if ("error" in parsed) {
      setError(parsed.error);
      return;
    }
    const iso = dateFrom ? dayjs(dateFrom).format("YYYY-MM-DD") : null;
    const createsInitial = target.mode === "create" && allowInitial && !iso;
    if (!isInitial && !iso && !createsInitial) {
      setError("Укажите дату «действует с».");
      return;
    }
    const body: RateInput = { rate: parsed.value, note: note.trim() };
    if (!isInitial) body.date_from = iso;
    const noteOnly =
      target.mode === "edit" &&
      parsed.value === Number(target.row.rate).toFixed(2) &&
      (isInitial || iso === target.row.date_from);
    setPending({ body, noteOnly });
    if (noteOnly) {
      setError(null);
      setImpact(null);
      setStep("confirm");
      return;
    }
    const rowId = target.mode === "edit" ? target.row.id : undefined;
    void loadImpact(isInitial ? null : iso, rowId).then((ok) => {
      if (ok) setStep("confirm");
    });
  };

  const handleConfirm = () => {
    setBusy(true);
    setError(null);
    let request: Promise<EmployeeRates>;
    let message: string;
    if (target.mode === "delete") {
      request = deleteEmployeeRate(target.row.id);
      message = "Перевод удалён.";
    } else if (target.mode === "edit") {
      // Правка одного примечания уходит только примечанием: иначе она
      // вернула бы ставку, которую другой сотрудник успел поменять, и мимо
      // шага последствий.
      const body: RateInput = pending?.noteOnly
        ? { note: pending.body.note ?? "" }
        : (pending?.body ?? {});
      request = updateEmployeeRate(target.row.id, body);
      message = "Изменения сохранены.";
    } else {
      request = createEmployeeRate(employeeId, pending?.body ?? {});
      message = "Перевод сохранён.";
    }
    request
      .then((data) => onSaved(data, message))
      .catch((e: Error) => {
        setError(e.message || "Не удалось сохранить.");
        setBusy(false);
      });
  };

  const noteUnchanged =
    target.mode === "edit" && (pending?.body.note ?? "") === (target.row.note ?? "").trim();

  const summary = (() => {
    if (target.mode === "delete") {
      const head = `Будет удалён перевод на ставку ${formatRate(target.row.rate)} с ${fmtDate(
        target.row.date_from,
      )}.`;
      const dateFrom = target.row.date_from;
      const hasPrevious = rows.some(
        (r) =>
          r.id !== target.row.id &&
          (r.is_initial || (r.date_from !== null && dateFrom !== null && r.date_from < dateFrom)),
      );
      if (hasPrevious) return `${head} С этой даты вернётся предыдущая ставка.`;
      // Без ставки неделю не отправить (гейт !365): это главное последствие,
      // а не «норма ноль». «До следующего перевода» — только если он есть.
      const hasNext = rows.some(
        (r) =>
          r.id !== target.row.id &&
          r.date_from !== null &&
          dateFrom !== null &&
          r.date_from > dateFrom,
      );
      const since = hasNext ? "С этой даты до следующего перевода" : "С этой даты";
      return `${head} Предыдущей ставки у сотрудника нет (строка «с начала» отсутствует). ${since} ставка не будет задана: сотрудник не сможет отправить таймшит за эти недели, автозаполнение их тоже не отправит, а дозаполнение и выгрузка в 1С покажут его как сотрудника без ставки. Чтобы этого избежать, сначала заведите ставку «с начала».`;
    }
    if (pending?.noteOnly) {
      return noteUnchanged
        ? "Изменений нет."
        : "Изменится только примечание. Ставка и дата не меняются, норма не пересчитывается.";
    }
    const rate = formatRate(pending?.body.rate ?? "");
    const since =
      isInitial || !pending?.body.date_from ? "с начала" : `с ${fmtDate(pending.body.date_from)}`;
    return target.mode === "create"
      ? `Перевод на ставку ${rate} ${since}.`
      : `Строка истории после изменения: ставка ${rate}, действует ${since}.`;
  })();

  const canConfirm = pending?.noteOnly ? !noteUnchanged : impact !== null;

  const confirmLabel =
    target.mode === "delete"
      ? "Удалить перевод"
      : target.mode === "edit"
        ? "Сохранить изменения"
        : "Сохранить перевод";

  return (
    <Modal opened onClose={onClose} title={MODAL_TITLE[target.mode]} size="lg" centered>
      {step === "form" ? (
        <Stack gap="sm">
          <TextInput
            label="Ставка"
            description="От 0,01 до 1,00"
            placeholder="например 0,5"
            value={rateText}
            onChange={(e) => setRateText(e.currentTarget.value)}
            data-autofocus
          />
          {isInitial ? (
            <TextInput
              label="Действует с"
              value="с начала"
              disabled
              description="Строке «с начала» дату задать нельзя: она определяет ставку на всё время до первого перевода."
            />
          ) : (
            <DateInput
              label="Действует с"
              placeholder="дд.мм.гггг"
              value={dateFrom}
              onChange={setDateFrom}
              valueFormat="DD.MM.YYYY"
              locale="ru"
              dateParser={parseRuDate}
              popoverProps={{ withinPortal: true }}
              aria-label="Действует с"
              clearable={allowInitial && target.mode === "create"}
              description={
                allowInitial && target.mode === "create"
                  ? "Если дату не указать, будет заведена строка «с начала»."
                  : undefined
              }
            />
          )}
          <TextInput
            label="Примечание"
            placeholder="Например, номер и дата приказа"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          {error && (
            <Alert color="red" data-testid="rate-editor-error">
              {error}
            </Alert>
          )}
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={onClose} disabled={busy}>
              Отмена
            </Button>
            <Button onClick={handleNext} loading={busy}>
              Далее
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          <Text size="sm" data-testid="rate-editor-summary">
            {summary}
          </Text>
          {pending?.noteOnly && target.mode === "edit" ? null : impact ? (
            <RateImpactNotice
              impact={impact}
              createsInitial={target.mode === "create" && !pending?.body.date_from}
            />
          ) : busy ? (
            <Loader size="sm" data-testid="rate-impact-loading" />
          ) : null}
          {error && (
            <Alert color="red" data-testid="rate-editor-error">
              {error}
              {target.mode === "delete" && !impact && (
                <Group mt="xs">
                  <Button size="xs" variant="default" onClick={loadDeleteImpact} disabled={busy}>
                    Повторить
                  </Button>
                </Group>
              )}
            </Alert>
          )}
          <Group justify="flex-end" gap="xs">
            {target.mode === "delete" ? (
              <Button variant="default" onClick={onClose} disabled={busy}>
                Отмена
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={() => {
                  setError(null);
                  setStep("form");
                }}
                disabled={busy}
              >
                Назад
              </Button>
            )}
            <Button
              color={target.mode === "delete" ? "red" : undefined}
              onClick={handleConfirm}
              loading={busy}
              disabled={!canConfirm}
            >
              {confirmLabel}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Экран
// ---------------------------------------------------------------------------

type Option = { value: string; label: string };

/** Пометки в списке поиска. «Не работает», а не «уволен»: форма одна для
 *  сотрудников и сотрудниц, и так же читается у тех, кого деактивировали без
 *  даты увольнения (до августа 2026). Тестовые учётки помечены, чтобы на проде
 *  их не приняли за людей. */
export function optionLabel(fullName: string, isActive: boolean, isTest = false): string {
  const marks: string[] = [];
  if (!isActive) marks.push("не работает");
  if (isTest) marks.push("тестовая учётная запись");
  return marks.length ? `${fullName} (${marks.join(", ")})` : fullName;
}

/** Метка в таблице истории не обрезается многоточием: у Badge по умолчанию
 *  overflow: hidden + text-overflow: ellipsis, и в тесной колонке смысл метки
 *  терялся. Места под неё хватает — она стоит отдельной строкой под датой. */
const FULL_BADGE_STYLES = {
  root: { overflow: "visible", flexShrink: 0 },
  label: { overflow: "visible", textOverflow: "clip" },
} as const;

/** Короче этого поиск на сервер не уходит. */
const MIN_SEARCH_LENGTH = 2;

/** Что сказать в пустом списке поиска. Пока запрос не набран — просим
 *  набрать; пока ответ не пришёл — «Поиск…», иначе «Никого не найдено»
 *  мелькнёт до ответа сервера, а пустота закрыла бы список; запрос не удался
 *  (сессия истекла, сервер ответил ошибкой, нет сети) — так и говорим, а не
 *  «никого»: иначе человека сочтут незаведённым; ответ пришёл пустым — «никого». */
export function searchEmptyMessage(query: string, searching: boolean, failed = false): string {
  if (query.trim().length < MIN_SEARCH_LENGTH) return "Начните вводить ФИО…";
  if (searching) return "Поиск…";
  if (failed) return "Не удалось выполнить поиск. Обновите страницу и попробуйте снова.";
  return "Никого не найдено";
}

export function RatesView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const employeeParam = searchParams.get("employee");
  const employeeId = employeeParam && /^\d+$/.test(employeeParam) ? Number(employeeParam) : null;

  const [options, setOptions] = useState<Option[]>([]);
  const [data, setData] = useState<EmployeeRates | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  // Номер последнего запроса: «идёт поиск» гасит и об ошибке сообщает только
  // ответ на него, а не запоздавший ответ на запрос, который пользователь уже
  // перепечатал.
  const searchSeq = useRef(0);

  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  useEffect(() => {
    setData(null);
    setError(null);
    setMessage(null);
    if (employeeId === null) return;
    let cancelled = false;
    setLoading(true);
    fetchEmployeeRates(employeeId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить историю ставки.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const seq = ++searchSeq.current;
    setSearchFailed(false);
    const query = q.trim();
    if (query.length < MIN_SEARCH_LENGTH) {
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(() => {
      searchRateEmployees(query)
        .then((rows) => {
          setOptions((prev) => {
            const seen = new Set(prev.map((o) => o.value));
            const merged = [...prev];
            for (const r of rows) {
              if (!seen.has(String(r.id))) {
                merged.push({
                  value: String(r.id),
                  label: optionLabel(r.full_name, r.is_active, r.is_test),
                });
              }
            }
            return merged;
          });
        })
        .catch(() => {
          if (seq === searchSeq.current) setSearchFailed(true);
        })
        .finally(() => {
          if (seq === searchSeq.current) setSearching(false);
        });
    }, 250);
  }, []);

  // Выбранный по ссылке сотрудник должен быть в списке, иначе Select пуст.
  const selectData = useMemo<Option[]>(() => {
    if (!data) return options;
    const value = String(data.employee.id);
    if (options.some((o) => o.value === value)) return options;
    return [
      { value, label: optionLabel(data.employee.full_name, data.employee.is_active) },
      ...options,
    ];
  }, [options, data]);

  const handleSelect = (value: string | null) => {
    setSearchParams(value ? { employee: value } : {});
  };

  const handleSaved = (next: EmployeeRates, text: string) => {
    setData(next);
    setEditor(null);
    setMessage(text);
  };

  const today = dayjs().format("YYYY-MM-DD");
  const emp = data?.employee;
  const hasInitial = data?.rates.some((r) => r.is_initial) ?? true;

  return (
    <Stack gap="md" maw={1000}>
      <Text size="sm" c="dimmed">
        Ставка определяет норму часов сотрудника на каждый день. Перевод действует с указанной даты
        до следующего перевода. Сотруднику ставка не показывается — он видит только норму недели в
        часах.
      </Text>
      <Select
        label="Сотрудник"
        placeholder="Начните вводить ФИО"
        searchable
        clearable
        data={selectData}
        value={employeeId !== null ? String(employeeId) : null}
        onChange={handleSelect}
        onSearchChange={handleSearch}
        nothingFoundMessage={searchEmptyMessage(searchQuery, searching, searchFailed)}
        maw={480}
        aria-label="Сотрудник"
      />

      {employeeId === null && (
        <Text c="dimmed" size="sm">
          Выберите сотрудника, чтобы просмотреть историю ставки.
        </Text>
      )}
      {loading && <Loader size="sm" />}
      {error && <Alert color="red">{error}</Alert>}

      {data && emp && (
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Group justify="space-between" align="flex-start">
              <div>
                <Group gap="xs">
                  <Text fw={600}>{emp.full_name}</Text>
                  {!emp.is_active && (
                    <Badge color="gray" variant="light" data-testid="employee-terminated">
                      Не работает
                    </Badge>
                  )}
                </Group>
                {emp.position && (
                  <Text size="sm" c="dimmed">
                    {emp.position}
                  </Text>
                )}
                <Text size="sm" c="dimmed">
                  {emp.hire_date ? `Дата приёма: ${fmtDate(emp.hire_date)}` : null}
                  {emp.hire_date && emp.termination_date ? " · " : null}
                  {emp.termination_date
                    ? `Дата увольнения: ${fmtDate(emp.termination_date)}`
                    : null}
                </Text>
              </div>
              <Stack gap={4} align="flex-end">
                <Text size="sm">
                  Ставка сегодня:{" "}
                  <b data-testid="current-rate">
                    {data.current_rate ? formatRate(data.current_rate) : "—"}
                  </b>
                </Text>
                <Button size="xs" onClick={() => setEditor({ mode: "create" })}>
                  Оформить перевод
                </Button>
              </Stack>
            </Group>

            {!hasInitial && (
              <Alert color="orange" data-testid="no-initial-rate">
                У сотрудника нет строки ставки «с начала»: норма его дней до первого перевода
                считается нулём. Оформите перевод без даты — будет заведена строка «с начала».
              </Alert>
            )}
            {message && (
              <Alert color="teal" withCloseButton onClose={() => setMessage(null)}>
                {message}
              </Alert>
            )}

            <ScrollArea type="auto">
              <Table striped withTableBorder miw={720} data-testid="rates-table">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Действует с</Table.Th>
                    <Table.Th style={{ textAlign: "right" }}>Ставка</Table.Th>
                    <Table.Th>Примечание</Table.Th>
                    <Table.Th>Кто завёл</Table.Th>
                    <Table.Th>Когда</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.rates.map((r) => (
                    <Table.Tr key={r.id} data-testid={`rate-row-${r.id}`}>
                      <Table.Td style={{ whiteSpace: "nowrap" }}>
                        {/* Метка — под датой, а не рядом: в строку с датой её
                            сжимало до «ДЕЙСТВУЕТ С…» (приёмка 25.09, окно ~800 px). */}
                        <Stack gap={4} align="flex-start">
                          <span>{r.is_initial ? "с начала" : fmtDate(r.date_from)}</span>
                          {r.is_current && (
                            <Badge
                              size="xs"
                              color="teal"
                              variant="light"
                              styles={FULL_BADGE_STYLES}
                              data-testid="rate-mark-current"
                            >
                              действует сейчас
                            </Badge>
                          )}
                          {!r.is_initial && r.date_from && r.date_from > today && (
                            <Badge
                              size="xs"
                              color="blue"
                              variant="light"
                              styles={FULL_BADGE_STYLES}
                              data-testid="rate-mark-planned"
                            >
                              запланирована
                            </Badge>
                          )}
                        </Stack>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "right" }}>{formatRate(r.rate)}</Table.Td>
                      <Table.Td>{r.note || "—"}</Table.Td>
                      <Table.Td>{r.created_by ?? "—"}</Table.Td>
                      <Table.Td>
                        {r.created_at ? dayjs(r.created_at).format("DD.MM.YYYY HH:mm") : "—"}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap" justify="flex-end">
                          <Tooltip label="Изменить строку">
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label="Изменить строку"
                              onClick={() => setEditor({ mode: "edit", row: r })}
                            >
                              <Pencil size={14} />
                            </ActionIcon>
                          </Tooltip>
                          {/* Строку «с начала» удалить нельзя (Р7): без неё ставка
                              до первого перевода не определена. */}
                          {!r.is_initial && (
                            <Tooltip label="Удалить перевод">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                aria-label="Удалить перевод"
                                onClick={() => setEditor({ mode: "delete", row: r })}
                              >
                                <Trash2 size={14} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        </Paper>
      )}

      {editor && data && (
        <RateEditor
          employeeId={data.employee.id}
          target={editor}
          rows={data.rates}
          allowInitial={!hasInitial}
          onClose={() => setEditor(null)}
          onSaved={handleSaved}
        />
      )}
    </Stack>
  );
}
